import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, Download, ListChecks, Search, Users } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import api from '../../../utils/api';
import toast from 'react-hot-toast';
import { isoDate } from '../../../utils/time';
import {
  PAGE_WIDE, PageHeader, KpiTile, SectionCard, EmptyState, ErrorState, TableSkeleton, money, num, plural,
} from '../../../components/ui';
import {
  TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TR, TD, TD_NUM, TFOOT_ROW, MoneyCell, TotalsCheck,
  DateRange, Segmented, RefreshButton, CHART, axisMoney, MoneyTooltip,
} from '../financeKit';

const TYPES = [
  { value: 'all', label: 'All' },
  { value: 'employee', label: 'Employees' },
  { value: 'contractor', label: 'Contractors' },
];

// Long names are shortened on the chart axis only; the tooltip shows the full name.
const axisName = (v) => { const s = String(v || ''); return s.length > 18 ? `${s.slice(0, 17)}…` : s; };

function downloadCSV(filename, headers, rows) {
  const lines = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// A labelled search box that lines up with the date fields beside it.
function SearchField({ id, label, value, onChange, placeholder }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-[12px] font-medium text-slate-500">{label}</label>
      <div className="relative">
        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <input id={id} type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="input-field !w-48 !py-2 !pl-8 text-sm" />
      </div>
    </div>
  );
}

export default function StaffPayReport() {
  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'employee' | 'contractor'
  const today = new Date();
  const [from, setFrom] = useState(() => {
    const d = new Date(today.getFullYear(), today.getMonth(), 1);
    return isoDate(d);
  });
  const [to, setTo] = useState(() => isoDate(today));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/financials/reports/staff-pay?from=${from}&to=${to}`);
      setData(r.data || []);
    } catch (e) {
      setData([]);
      setError(e.response?.data?.message || "We couldn't load the staff pay report. Check your connection, then try again.");
    } finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const filtered = data.filter(d => {
    if (typeFilter === 'contractor' && d.employment_type !== 'contractor') return false;
    if (typeFilter === 'employee'   && d.employment_type === 'contractor') return false;
    if (search && !d.staff_name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const chartData = filtered.slice(0, 15).map(d => ({
    name: d.staff_name || '—',
    pay: parseFloat(d.total_pay || 0),
    super: parseFloat(d.total_super || 0),
  }));

  const totalPay   = filtered.reduce((s, d) => s + parseFloat(d.total_pay || 0), 0);
  const totalSuper = filtered.reduce((s, d) => s + parseFloat(d.total_super || 0), 0);
  const totalCost  = filtered.reduce((s, d) => s + parseFloat(d.total_cost || d.total_pay || 0), 0);

  // Each table row worked out once, so the rows, the totals row and the check below agree.
  const rows = filtered.map((row) => {
    const isContractor = row.employment_type === 'contractor';
    const pay      = parseFloat(row.total_pay || 0);
    const superAmt = isContractor ? 0 : parseFloat(row.total_super || 0);
    const cost     = pay + superAmt;
    const avgShift = row.shifts ? (pay / row.shifts) : 0;
    const avgRate  = row.total_hours ? (pay / row.total_hours) : 0;
    return { row, isContractor, pay, superAmt, cost, avgShift, avgRate };
  });
  const sumShifts = rows.reduce((s, r) => s + (parseInt(r.row.shifts, 10) || 0), 0);
  const sumHours  = rows.reduce((s, r) => s + (parseFloat(r.row.total_hours) || 0), 0);
  const sumPay    = rows.reduce((s, r) => s + r.pay, 0);
  const sumSuper  = rows.reduce((s, r) => s + r.superAmt, 0);
  const sumCost   = rows.reduce((s, r) => s + r.cost, 0);
  const contractorCount = rows.filter((r) => r.isContractor).length;

  // The tiles count any super recorded against a contractor; the table shows contractors as N/A.
  // If that ever makes the two disagree, say so rather than show two different totals.
  const checks = [
    { label: 'Super', computed: sumSuper, expected: totalSuper },
    { label: 'Total cost', computed: sumCost, expected: totalCost },
  ];

  function exportCSV() {
    const headers = ['Staff Member','Shifts','Hours','Total Pay','Super','Total Cost','Avg/Shift','Avg Rate/Hr'];
    const rows = filtered.map(row => {
      const pay = parseFloat(row.total_pay || 0);
      const superAmt = parseFloat(row.total_super || 0);
      const cost = parseFloat(row.total_cost || pay);
      return [row.staff_name, row.shifts, Number(row.total_hours || 0).toFixed(1),
        pay.toFixed(2), superAmt.toFixed(2), cost.toFixed(2),
        (row.shifts ? pay / row.shifts : 0).toFixed(2),
        (row.total_hours ? pay / row.total_hours : 0).toFixed(2)];
    });
    downloadCSV(`staff-pay-${from}-to-${to}.csv`, headers, rows);
    toast.success(`Exported ${plural(filtered.length, 'staff member')}`);
  }

  const filteredOut = !loading && data.length > 0 && filtered.length === 0;
  const clearFilters = () => { setSearch(''); setTypeFilter('all'); };

  return (
    <div className={PAGE_WIDE}>
      <PageHeader
        title="Staff pay"
        description="Pay and super for each staff member, from shifts with an approved timesheet."
        actions={(
          <>
            <Segmented ariaLabel="Employment type" options={TYPES} value={typeFilter} onChange={setTypeFilter} />
            <SearchField id="staffpay-search" label="Search" value={search} onChange={setSearch} placeholder="Staff name" />
            <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} idPrefix="staffpay" />
            <RefreshButton onClick={load} loading={loading} label="Refresh report" />
            <button type="button" onClick={exportCSV} disabled={filtered.length === 0} className="btn-secondary">
              <Download size={14} /> Export CSV
            </button>
          </>
        )}
      />

      {error ? (
        <div className="card"><ErrorState message={error} onRetry={load} /></div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile label="Staff pay" value={loading ? '…' : money(totalPay)} accent="slate"
              sub={loading ? null : `${num(sumShifts)} shifts · ${num(sumHours, 1)} h`} />
            <KpiTile label="Super" value={loading ? '…' : money(totalSuper)} accent="slate" />
            <KpiTile label="Total employment cost" value={loading ? '…' : money(totalCost)} accent="primary"
              sub={loading ? null : 'Pay plus super'} />
            <KpiTile label="Staff members" value={loading ? '…' : num(filtered.length)} accent="slate"
              sub={loading ? null : `${num(filtered.length - contractorCount)} employees · ${num(contractorCount)} contractors`} />
          </div>

          {!loading && chartData.length > 0 && (
            <SectionCard title={`Pay and super by staff member${filtered.length > 15 ? ' · top 15' : ''}`} icon={BarChart3} bodyClass="p-4">
              <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 30 + 40)}>
                <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 12, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: CHART.axis }} tickFormatter={axisMoney} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: CHART.axis }} tickFormatter={axisName} width={128} tickLine={false} />
                  <Tooltip content={<MoneyTooltip />} cursor={{ fill: 'rgba(148,163,184,.12)' }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="pay" name="Pay" fill={CHART.revenue} radius={[0, 3, 3, 0]} maxBarSize={14} isAnimationActive={false} />
                  <Bar dataKey="super" name="Super" fill={CHART.staff} radius={[0, 3, 3, 0]} maxBarSize={14} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </SectionCard>
          )}

          <SectionCard title={`Staff pay detail${loading ? '' : ` · ${rows.length}`}`} icon={ListChecks}>
            {loading ? (
              <TableSkeleton rows={6} cols={8} />
            ) : data.length === 0 ? (
              <EmptyState icon={Users} title="No staff pay in this range"
                hint="Staff appear here once their shifts have an approved timesheet. If approved shifts are missing, run Recalculate on the Financials dashboard." />
            ) : filteredOut ? (
              <EmptyState icon={Search} title="No staff match these filters"
                hint="Check the spelling, or show every employment type."
                action={<button type="button" onClick={clearFilters} className="btn-secondary">Clear filters</button>} />
            ) : (
              <div className={TABLE_WRAP}>
                <table className={TABLE}>
                  <thead>
                    <tr className={THEAD_ROW}>
                      <th scope="col" className={TH}>Staff member</th>
                      <th scope="col" className={TH_NUM}>Shifts</th>
                      <th scope="col" className={TH_NUM}>Hours</th>
                      <th scope="col" className={TH_NUM}>Pay</th>
                      <th scope="col" className={TH_NUM}>Super</th>
                      <th scope="col" className={TH_NUM}>Total cost</th>
                      <th scope="col" className={TH_NUM}>Avg per shift</th>
                      <th scope="col" className={TH_NUM}>Avg per hour</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ row, isContractor, pay, superAmt, cost, avgShift, avgRate }, i) => (
                      <tr key={row.id || i} className={TR}>
                        <td className={TD}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800">{row.staff_name}</span>
                            {isContractor && <span className="badge-gray">Contractor</span>}
                          </div>
                          {isContractor && row.abn && (
                            <p className="text-[12px] text-slate-500 mt-0.5">ABN {row.abn}</p>
                          )}
                        </td>
                        <td className={TD_NUM}>{num(row.shifts)}</td>
                        <td className={TD_NUM}>{num(row.total_hours, 1)}</td>
                        <td className={TD_NUM}><MoneyCell value={pay} /></td>
                        <td className={TD_NUM}>
                          {isContractor
                            ? <span className="text-slate-500" title="Contractors are not paid super">N/A</span>
                            : <MoneyCell value={superAmt} />}
                        </td>
                        <td className={TD_NUM}><MoneyCell value={cost} strong /></td>
                        <td className={TD_NUM}><MoneyCell value={avgShift} /></td>
                        <td className={TD_NUM}><MoneyCell value={avgRate} /></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={TFOOT_ROW}>
                      <td className={TD}>Total</td>
                      <td className={TD_NUM}>{num(sumShifts)}</td>
                      <td className={TD_NUM}>{num(sumHours, 1)}</td>
                      <td className={TD_NUM}><MoneyCell value={sumPay} strong /></td>
                      <td className={TD_NUM}><MoneyCell value={sumSuper} strong /></td>
                      <td className={TD_NUM}><MoneyCell value={sumCost} strong /></td>
                      <td className={TD_NUM}><MoneyCell value={sumShifts ? sumPay / sumShifts : 0} strong /></td>
                      <td className={TD_NUM}><MoneyCell value={sumHours ? sumPay / sumHours : 0} strong /></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </SectionCard>

          {!loading && rows.length > 0 && <TotalsCheck checks={checks} />}
        </>
      )}
    </div>
  );
}
